import { resizeModelWidthDepth } from '../modelGeometry/modelWidthDepthResizer';
import { applyCabinetMaterialUvProjection } from '../modelGeometry/cabinetMaterialUvProjector';
import { resizeStraightFrontMeshesNineSlice } from '../modelGeometry/straightFrontMeshNineSliceResizer';
import { SHAPE_CORNER_LAYOUTS, SHAPE_SQUARE_LAYOUTS } from '../../../../../../canvas/core/cabinet/cabinetShapes';
import { resizeCornerModelWidthDepth } from './tallLCornerCabinetWidthDepthResizer';

const ORIGINAL_GEOMETRY_TAG = 'tallCabinetSizeOriginalGeometry';
const ORIGINAL_BOUNDS_TAG = 'tallCabinetSizeOriginalBounds';
const SIDE_BAND_RATIO = 0.12;
const FRONT_SIDE_BAND_RATIO = 0.28;

// Square tall cabinets keep their original GLB depth; depth changes represent cabinet layout offset, not model thickness.
function getTallCabinetTargetDepth(shape) {
  if (shape.shapeParams && SHAPE_SQUARE_LAYOUTS.includes(shape.shapeParams.shapeLayout)) {
    return shape.getInitialDepth();
  }

  return shape.getDepth();
}

function isCornerTallCabinet(shape) {
  const shapeLayout = shape.shapeParams && (shape.shapeParams.shapeLayout || shape.shapeParams.layout);
  return shapeLayout && SHAPE_CORNER_LAYOUTS.includes(shapeLayout);
}

function getFrontLineIndexes(shape) {
  if (!shape || !shape.shapeCabinet || typeof shape.shapeCabinet.getFrontLines !== 'function') {
    return [];
  }

  const lines = shape.getLines();
  const frontLines = shape.shapeCabinet.getFrontLines();

  return frontLines
    .map((frontLine) => lines.indexOf(frontLine))
    .filter((lineIndex) => lineIndex >= 0);
}

// Material UV correction is only needed when geometry was actually resized.
function hasCustomWidthDepth(shape) {
  const normalizedInitialWidth = Number(shape.getInitialWidth && shape.getInitialWidth());
  const normalizedWidth = Number(shape.getWidth && shape.getWidth());
  const normalizedInitialDepth = Number(shape.getInitialDepth && shape.getInitialDepth());
  const normalizedDepth = Number(shape.getDepth && shape.getDepth());

  if (normalizedInitialWidth && normalizedWidth && Math.abs(normalizedWidth - normalizedInitialWidth) > 0.001) {
    return true;
  }

  if (normalizedInitialDepth && normalizedDepth && Math.abs(normalizedDepth - normalizedInitialDepth) > 0.001) {
    const shapeLayout = shape.shapeParams && shape.shapeParams.shapeLayout;

    return !SHAPE_SQUARE_LAYOUTS.includes(shapeLayout);
  }

  return false;
}

// Tall cabinets use regular box resizing unless the 2D shape is a corner layout.
export function applyTallCabinetWidthDepth(instance, shape) {
  if (!instance || !shape) {
    return;
  }

  if (isCornerTallCabinet(shape)) {
    const frontLineIndexes = getFrontLineIndexes(shape);

    resizeCornerModelWidthDepth(
      instance,
      {
        originalPoints: shape.getOriginalPointsCoords(),
        currentPoints: shape.getPointsCoords(),
        frontLineIndexes,
      },
      {
        boundsTag: ORIGINAL_BOUNDS_TAG,
        geometryTag: ORIGINAL_GEOMETRY_TAG,
        sideBandRatio: SIDE_BAND_RATIO,
        frontSideBandRatio: FRONT_SIDE_BAND_RATIO,
      }
    );
    if (hasCustomWidthDepth(shape)) {
      applyCabinetMaterialUvProjection(instance, shape);
    }
    return;
  }

  resizeModelWidthDepth(
    instance,
    {
      initialWidth: shape.getInitialWidth(),
      width: shape.getWidth(),
      initialDepth: shape.getInitialDepth(),
      depth: getTallCabinetTargetDepth(shape),
    },
    {
      boundsTag: ORIGINAL_BOUNDS_TAG,
      geometryTag: ORIGINAL_GEOMETRY_TAG,
      sideBandRatio: SIDE_BAND_RATIO,
    }
  );
  resizeStraightFrontMeshesNineSlice(instance, {
    geometryTag: ORIGINAL_GEOMETRY_TAG,
    sideBandRatio: FRONT_SIDE_BAND_RATIO,
  });
  if (hasCustomWidthDepth(shape)) {
    applyCabinetMaterialUvProjection(instance, shape);
  }
}
